import { Key, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { MdOutlineMoreHoriz } from 'react-icons/md';
import { SortDescriptor } from '@ds';
import { ActionMenu, TableColumnType } from '@ui';
import { makeCellTextRenderers } from '@features/components/TableCellTextMR';
import { OpoOrganization, OpoOrganizationListFilters, OpoOrganizationOrderByOption } from '@/api/apis/opo';
import { getShortTzByIanaName } from '@/utils/helpers';
import { buildEditDeleteActionsMenu } from '../../components/EditDeleteActionsMenu';
import { col, COMMON_COLUMN_PRESETS } from '../../utils';

export const OPOS_LIST_FILTERS_BASE: Required<OpoOrganizationListFilters> = {
  organizationIds: [],
  countries: [],
  states: [],
};

export const OPOS_LIST_DEFAULT_SORT_BY: SortDescriptor = {
  column: OpoOrganizationOrderByOption.ModifiedAt,
  direction: 'descending',
};

export const OPOS_TABLE_COLUMNS: TableColumnType[] = [
  col('name', 'OPO name', {
    sortable: true,
    minWidthClassName: 'min-w-80',
  }),
  COMMON_COLUMN_PRESETS.code,
  COMMON_COLUMN_PRESETS.timezone,
  COMMON_COLUMN_PRESETS.address,
  COMMON_COLUMN_PRESETS.city,
  COMMON_COLUMN_PRESETS.state,
  COMMON_COLUMN_PRESETS.postalCode,
  COMMON_COLUMN_PRESETS.actions,
];

export const getRenderOPOsTableCell =
  (
    onEdit: (hospitalId: string) => void,
    onDelete: (hospitalId: string) => void,
    resolveSubdivisionName: (stateCode?: string | null, countryCode?: string | null) => string | undefined,
  ) =>
  (opos: OpoOrganization, columnKey: Key): ReactNode => {
    const { id, name, code, location } = opos;

    const { text, textU } = makeCellTextRenderers();

    const actions = buildEditDeleteActionsMenu({
      editConfig: {
        key: 'edit-opo',
        to: 'edit-opo',
        onAction: () => onEdit(id),
      },
      deleteConfig: {
        key: 'delete-opo',
        to: 'delete-opo',
        onAction: () => onDelete(id),
      },
    });

    switch (columnKey) {
      case 'name':
        return (
          <Link to={`/admin/opos/locations/${id}`} className="flex items-center gap-2">
            <span className="text-m-m">{name}</span>
          </Link>
        );

      case 'code':
        return textU(code || '-');

      case 'timezone':
        return textU(getShortTzByIanaName(location?.timezone) || '-');

      case 'address':
        return text(location?.addressLine || '-');

      case 'city':
        return text(location?.city || '-');

      case 'state':
        return textU(resolveSubdivisionName(location?.state, location?.country) || '-');
      case 'postalCode':
        return text(location?.postalCode || '-');

      case 'actions':
        return (
          <ActionMenu
            placement="left-start"
            items={actions}
            MenuTrigger={<MdOutlineMoreHoriz size={20} />}
            label={`${name} actions menu`}
          />
        );

      default:
        return null;
    }
  };
